import React, { useState, useMemo } from "react";
import { Info, CursorClick } from "@phosphor-icons/react";

// Build a synthetic Market Profile (TPO) bell-curve: rows are price levels (descending),
// each row contains TPO letters (one per 30-min bracket) that occurred at that price.
// Returns: [{ price, letters: ["A","B"...] }]
function buildProfile() {
  const letters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
  const startPrice = 24400;
  const tickSize = 5;
  const rows = 26;

  // Distribution counts per row to form a rough bell curve
  const dist = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 10, 11, 11, 10, 9, 9, 8, 7, 6, 5, 4, 3, 2, 1, 1];
  // letters distribution per row (which brackets touched this price), constrained
  return Array.from({ length: rows }).map((_, i) => {
    const count = dist[i] || 1;
    const offset = Math.floor((letters.length - count) * (0.3 + Math.random() * 0.4));
    const rowLetters = letters.slice(Math.max(0, offset), Math.max(0, offset) + count);
    const price = startPrice - i * tickSize;
    return { price, letters: rowLetters };
  });
}

const PROFILE = buildProfile();

// Find POC (longest row), VAH/VAL approx (70% volume)
const POC_INDEX = PROFILE.reduce(
  (acc, r, i) => (r.letters.length > PROFILE[acc].letters.length ? i : acc),
  0
);
const VA_LOW = Math.min(POC_INDEX + 4, PROFILE.length - 1);
const VA_HIGH = Math.max(POC_INDEX - 5, 0);

const LETTER_INFO = {
  A: "09:15–09:45 IST — Initial Balance starts. Strong sellers tested the high.",
  B: "09:45–10:15 — Range extension upward. Buyers stepped in.",
  C: "10:15–10:45 — Acceptance near POC. Two-way auction.",
  D: "10:45–11:15 — Aggressive imbalance to upside. Delta positive.",
  E: "11:15–11:45 — Value migrating higher. Initiative buying.",
  F: "11:45–12:15 — Lunch rotation. Low volume node forming.",
  G: "12:15–12:45 — Absorption at VAH. Sellers absorbed.",
  H: "12:45–13:15 — Late buyers trapped. Watch for rejection.",
  I: "13:15–13:45 — Composite POC retest.",
  J: "13:45–14:15 — Initiative selling, value rejection.",
  K: "14:15–14:45 — Stop-run below VAL. CVD divergent.",
  L: "14:45–15:15 — Closing auction. Day-type confirms.",
};

export default function InteractivePreview() {
  const [active, setActive] = useState("D");

  const maxLen = useMemo(
    () => Math.max(...PROFILE.map((r) => r.letters.length)),
    []
  );

  return (
    <section
      data-testid="preview-section"
      id="preview"
      className="bg-[#0A0A0A] text-[#F4F3ED] py-20 lg:py-28 border-y border-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{
             backgroundImage:
               "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
             backgroundSize: "40px 40px",
           }}
      />
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
              04 / INTERACTIVE PREVIEW
            </div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              Read the
              <br />
              <span className="text-[#D9381E]">profile.</span>
            </h2>
          </div>
          <p className="font-body text-[#A1A1AA] max-w-md">
            Hover or tap any letter (a TPO bracket) on the profile. This is a
            taste of what every market day will look like once you graduate.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* TPO chart */}
          <div className="lg:col-span-8 border border-[#2A2A2A] bg-[#0E0E0E]">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2A2A2A]">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D9381E] rounded-full" />
                <span className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA]">
                  NIFTY • TPO PROFILE • 22 FEB 2026
                </span>
              </div>
              <span className="font-mono-acc text-[10px] tracking-[0.25em] text-[#A1A1AA]">
                30-MIN BRACKETS
              </span>
            </div>

            <div className="p-4 lg:p-6 overflow-x-auto thin-scroll">
              <div className="min-w-[560px]">
                {PROFILE.map((row, i) => {
                  const isPOC = i === POC_INDEX;
                  const inVA = i >= VA_HIGH && i <= VA_LOW;
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 group"
                    >
                      <span
                        className={`font-mono-acc text-[10px] w-16 text-right tabular-nums ${
                          isPOC ? "text-[#D9381E] font-bold" : "text-[#A1A1AA]"
                        }`}
                      >
                        {row.price.toFixed(2)}
                      </span>
                      <span
                        className={`font-mono-acc text-[10px] w-6 ${
                          isPOC
                            ? "text-[#D9381E]"
                            : inVA
                            ? "text-[#F4F3ED]"
                            : "text-[#4A4A4A]"
                        }`}
                      >
                        {isPOC ? "POC" : inVA ? "VA" : ""}
                      </span>
                      <div className="flex items-center">
                        {row.letters.map((l, k) => {
                          const isActive = l === active;
                          return (
                            <button
                              key={k}
                              data-testid={`tpo-${i}-${k}`}
                              onMouseEnter={() => setActive(l)}
                              onClick={() => setActive(l)}
                              className={`tpo-cell border border-transparent transition-all duration-150 ${
                                isActive
                                  ? "bg-[#D9381E] text-white scale-110 z-10"
                                  : isPOC
                                  ? "bg-[#4A1410] text-[#F4F3ED] hover:bg-[#D9381E]"
                                  : inVA
                                  ? "bg-[#2A2A2A] text-[#F4F3ED] hover:bg-[#D9381E]"
                                  : "bg-[#1A1A1A] text-[#A1A1AA] hover:bg-[#D9381E] hover:text-white"
                              }`}
                              style={{ marginRight: 1 }}
                            >
                              {l}
                            </button>
                          );
                        })}
                        {/* spacer to make rows align */}
                        <span
                          style={{
                            width: `${(maxLen - row.letters.length) * 19}px`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-[#2A2A2A] px-4 py-3 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-[#D9381E]" />
                <span className="font-mono-acc text-[10px] tracking-[0.2em] text-[#A1A1AA] uppercase">
                  POC
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-[#2A2A2A]" />
                <span className="font-mono-acc text-[10px] tracking-[0.2em] text-[#A1A1AA] uppercase">
                  Value Area
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-[#1A1A1A] border border-[#2A2A2A]" />
                <span className="font-mono-acc text-[10px] tracking-[0.2em] text-[#A1A1AA] uppercase">
                  Single Prints
                </span>
              </div>
              <span className="flex items-center gap-2 ml-auto text-[10px] font-mono-acc tracking-[0.2em] text-[#D9381E]">
                <CursorClick size={14} weight="bold" /> Tap letters
              </span>
            </div>
          </div>

          {/* Side panel — TPO meaning */}
          <div className="lg:col-span-4 border border-[#2A2A2A] bg-[#0E0E0E] p-6 flex flex-col">
            <div className="font-mono-acc text-[10px] tracking-[0.3em] text-[#A1A1AA] mb-3 uppercase">
              Bracket Active
            </div>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-display font-black text-6xl text-[#D9381E] leading-none">
                {active}
              </span>
              <span className="font-mono-acc text-xs text-[#A1A1AA]">
                30-min TPO
              </span>
            </div>
            <p
              data-testid="tpo-info"
              className="font-body text-sm text-[#F4F3ED] leading-relaxed mb-6"
            >
              {LETTER_INFO[active]}
            </p>
            <div className="mt-auto border-t border-[#2A2A2A] pt-5">
              <div className="flex items-start gap-3 text-[#A1A1AA]">
                <Info size={18} weight="duotone" className="text-[#D9381E] mt-0.5 shrink-0" />
                <p className="font-body text-xs leading-relaxed">
                  In the full program you'll learn to identify each bracket's
                  intent in real time — and stack confluences from Order Flow
                  before pulling the trigger.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
